import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Send, Clock, CheckCircle, AlertTriangle, Mail, Zap } from "lucide-react";

interface PendingFollowup {
  id: string;
  email: string;
  type: string;
  credentials_sent_at: string | null;
  followup_sent_at: string | null;
  mass_emails_sent: any;
  hoursElapsed: number;
  pendingAction: "test_expired" | "special_offer" | null;
}

const AdminAutoEmailsSection = () => {
  const [loading, setLoading] = useState(true);
  const [pendingFollowups, setPendingFollowups] = useState<PendingFollowup[]>([]);
  const [lastCronRun, setLastCronRun] = useState<string | null>(null);
  const [runningCron, setRunningCron] = useState(false);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingFollowups();
  }, []);

  const fetchPendingFollowups = async () => {
    setLoading(true);
    try {
      // Get all test requests with credentials sent that haven't completed the email sequence
      const { data: requests, error } = await supabase
        .from("requests")
        .select("*")
        .eq("type", "test_24h")
        .eq("status", "credentials_sent")
        .is("converted_at", null)
        .order("credentials_sent_at", { ascending: true });

      if (error) throw error;

      const now = new Date();
      const pending: PendingFollowup[] = [];

      for (const req of requests || []) {
        if (!req.credentials_sent_at) continue;

        const credentialsSentAt = new Date(req.credentials_sent_at);
        const hoursElapsed = (now.getTime() - credentialsSentAt.getTime()) / (1000 * 60 * 60);

        // Parse mass_emails_sent safely (handles [], null, {})
        let massEmailsSent: Record<string, string> = {};
        if (req.mass_emails_sent) {
          if (Array.isArray(req.mass_emails_sent) && req.mass_emails_sent.length === 0) {
            massEmailsSent = {};
          } else if (typeof req.mass_emails_sent === 'object' && !Array.isArray(req.mass_emails_sent)) {
            massEmailsSent = req.mass_emails_sent as Record<string, string>;
          }
        }
        
        // Skip if all emails have been sent (promo already sent)
        if (massEmailsSent.special_offer) {
          continue;
        }

        let pendingAction: "test_expired" | "special_offer" | null = null;

        // Test expired: 24h after credentials, no followup sent yet
        if (hoursElapsed >= 24 && !req.followup_sent_at) {
          pendingAction = "test_expired";
        }
        // Special offer: 48h after test_expired was sent (72h total)
        else if (req.followup_sent_at) {
          const followupSentAt = new Date(req.followup_sent_at);
          const hoursSinceFollowup = (now.getTime() - followupSentAt.getTime()) / (1000 * 60 * 60);
          
          if (hoursSinceFollowup >= 48) {
            pendingAction = "special_offer";
          }
        }

        // Only show if there's a pending action
        if (pendingAction) {
          pending.push({
            id: req.id,
            email: req.email,
            type: req.type,
            credentials_sent_at: req.credentials_sent_at,
            followup_sent_at: req.followup_sent_at,
            mass_emails_sent: req.mass_emails_sent,
            hoursElapsed: Math.floor(hoursElapsed),
            pendingAction,
          });
        }
      }

      setPendingFollowups(pending);
    } catch (error) {
      console.error("Error fetching pending followups:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const triggerCronJob = async () => {
    setRunningCron(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-expired-tests");

      if (error) throw error;

      console.log("Cron job result:", data);
      setLastCronRun(new Date().toISOString());
      toast.success(`Traitement terminé: ${data?.processed || 0} email(s) envoyé(s)`);
      
      // Refresh the list
      await fetchPendingFollowups();
    } catch (error: any) {
      console.error("Error triggering cron:", error);
      toast.error("Erreur lors de l'exécution: " + error.message);
    } finally {
      setRunningCron(false);
    }
  };

  const sendManualEmail = async (followup: PendingFollowup) => {
    if (!followup.pendingAction) return;

    setSendingEmail(followup.id);
    try {
      const emailType = followup.pendingAction === "test_expired" ? "test_expired" : "special_offer";
      
      console.log(`Sending manual ${emailType} email to ${followup.email}...`);
      
      const { data, error } = await supabase.functions.invoke("send-followup-email", {
        body: {
          email: followup.email,
          type: emailType,
        },
      });

      console.log("send-followup-email response:", { data, error });

      if (error) throw error;

      // Check if response contains an error from Resend
      if (data?.error) {
        throw new Error(data.error.message || data.error);
      }

      // Update the request
      if (emailType === "test_expired") {
        const { error: updateError } = await supabase
          .from("requests")
          .update({ followup_sent_at: new Date().toISOString() })
          .eq("id", followup.id);
        
        if (updateError) {
          console.error("DB update error:", updateError);
        }
      } else {
        // Parse existing mass_emails_sent safely
        let currentMassEmails: Record<string, string> = {};
        if (followup.mass_emails_sent) {
          if (Array.isArray(followup.mass_emails_sent) && followup.mass_emails_sent.length === 0) {
            currentMassEmails = {};
          } else if (typeof followup.mass_emails_sent === 'object' && !Array.isArray(followup.mass_emails_sent)) {
            currentMassEmails = followup.mass_emails_sent as Record<string, string>;
          }
        }
        
        const { error: updateError } = await supabase
          .from("requests")
          .update({ 
            mass_emails_sent: {
              ...currentMassEmails,
              special_offer: new Date().toISOString()
            }
          })
          .eq("id", followup.id);
        
        if (updateError) {
          console.error("DB update error:", updateError);
        }
      }

      toast.success(`Email "${emailType}" envoyé à ${followup.email}`);
      await fetchPendingFollowups();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error sending email:", errorMessage);
      toast.error("Erreur: " + errorMessage);
    } finally {
      setSendingEmail(null);
    }
  };

  const getStatusBadge = (followup: PendingFollowup) => {
    if (followup.pendingAction === "test_expired") {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">⚠️ Relance 24h à envoyer</Badge>;
    }
    if (followup.pendingAction === "special_offer") {
      return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">🎁 Promo à envoyer</Badge>;
    }
    return null;
  };

  const pendingCount = pendingFollowups.filter(f => f.pendingAction).length;

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <p className="text-muted-foreground text-sm">Cron automatique</p>
            </div>
            <p className="text-lg font-semibold text-foreground">Toutes les heures</p>
            <p className="text-xs text-muted-foreground">
              {lastCronRun ? `Dernier: ${new Date(lastCronRun).toLocaleTimeString('fr-FR')}` : "Non déclenché manuellement"}
            </p>
          </CardContent>
        </Card>

        <Card className={`bg-card border-2 ${pendingCount > 0 ? 'border-amber-500' : 'border-green-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {pendingCount > 0 ? (
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-400" />
              )}
              <p className="text-muted-foreground text-sm">Emails en attente</p>
            </div>
            <p className={`text-3xl font-bold ${pendingCount > 0 ? 'text-amber-400' : 'text-green-400'}`}>
              {pendingCount}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4 flex flex-col justify-between h-full">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-primary" />
              <p className="text-muted-foreground text-sm">Action manuelle</p>
            </div>
            <Button 
              onClick={triggerCronJob} 
              disabled={runningCron}
              className="w-full"
            >
              {runningCron ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {runningCron ? "Envoi en cours..." : "Envoyer maintenant"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Explanation */}
      <Card className="bg-muted/50 border-border">
        <CardContent className="p-4">
          <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Séquence d'emails automatiques
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>📧 <strong>Email "Test expiré"</strong>: Envoyé 24h après réception des identifiants test</li>
            <li>🎁 <strong>Email "Offre spéciale"</strong>: Envoyé 48h après l'email "Test expiré" (72h total)</li>
            <li>⚡ Le cron automatique s'exécute toutes les heures. Utilisez le bouton ci-dessus pour forcer l'envoi.</li>
          </ul>
        </CardContent>
      </Card>

      {/* Pending Followups Table */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-foreground">Emails en attente d'envoi</CardTitle>
            <CardDescription>Uniquement les tests qui n'ont pas reçu leurs emails automatiques</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchPendingFollowups} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : pendingFollowups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucun test récent à suivre
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground">Identifiants envoyés</TableHead>
                    <TableHead className="text-muted-foreground">Temps écoulé</TableHead>
                    <TableHead className="text-muted-foreground">Statut</TableHead>
                    <TableHead className="text-muted-foreground">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingFollowups.map((followup) => (
                    <TableRow key={followup.id} className="border-border">
                      <TableCell className="text-foreground font-medium">
                        {followup.email}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {followup.credentials_sent_at
                          ? new Date(followup.credentials_sent_at).toLocaleString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {followup.hoursElapsed}h
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(followup)}
                      </TableCell>
                      <TableCell>
                        {followup.pendingAction ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendManualEmail(followup)}
                            disabled={sendingEmail === followup.id}
                            className="text-xs"
                          >
                            {sendingEmail === followup.id ? (
                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Send className="w-3 h-3 mr-1" />
                            )}
                            {followup.pendingAction === "test_expired" ? "Relance 24h" : "Promo -20%"}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAutoEmailsSection;
